import React from 'react';
import { useIntl } from 'react-intl';
import {
  apiHooks,
  useApiClient,
} from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import { useMixpanel } from '@lifeomic/phc-web-toolkit/src/hooks/useMixpanel';
import { bucketClientObservedDuration } from '@lifeomic/phc-web-toolkit/src/util/clientObservedGeneration';
import { getAuditSubjectMemberId } from '../AIRules/utils';
import { memberDetailMessages } from '../Members/MemberDetail/messages';

// Async-task agent name for the parent Zori Pulse scan, as recorded in the
// agents audit task store (matches the member Pulse page and scan-tasks route).
const ZORI_PULSE_SCAN_AGENT_NAME = 'zori-pulse-scan';
const ZORI_PULSE_SCAN_POLL_INTERVAL_MS = 3000;
const ZORI_PULSE_SCAN_TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'expired',
]);

type PulseScanEntryPoint = 'practitioner_queue' | 'member_pulse';

type ZoriPulseScanInvocation = {
  task_id: string;
  status: string;
};

type UseMemberPulseScanArgs = {
  patientId: string;
  // Project the scan runs against; required to invoke a new scan.
  projectId: string;
  // Gate: only the member Pulse permissions (feature + policy) enable scanning.
  // When false the hook stays inert (no audit query, no invoke).
  enabled: boolean;
  entryPoint: PulseScanEntryPoint;
  // Scan scope sent on invoke; omitted means the agent's default (coaching).
  scope?: 'ops';
  // Called once a polled scan reaches a terminal status, so the caller can
  // refetch data the scan produces.
  onScanSettled?: () => void;
};

type UseMemberPulseScan = {
  // Epoch ms of the most recent completed scan, or null if none on record.
  lastCompletedScanAt: number | null;
  isLoadingLastRun: boolean;
  isRunning: boolean;
  triggerScan: () => void;
};

/**
 * Per-member Zori Pulse scan controller for the Member Priorities list.
 *
 * Mirrors the member Pulse page: derives the last completed run from the agents
 * audit-task store (the `invocations` endpoint only returns in-flight scans),
 * invokes a new scan, polls it to completion, and refreshes the last-run line.
 * On mount it also detects an already in-flight scan so the row reflects the
 * running state instead of letting the operator trigger a duplicate (409).
 */
export const useMemberPulseScan = ({
  patientId,
  projectId,
  enabled,
  entryPoint,
  scope,
  onScanSettled,
}: UseMemberPulseScanArgs): UseMemberPulseScan => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const apiClient = useApiClient();
  const { track } = useMixpanel();

  // Held in a ref so an inline callback does not restart the polling interval.
  const onScanSettledRef = React.useRef(onScanSettled);
  React.useEffect(() => {
    onScanSettledRef.current = onScanSettled;
  }, [onScanSettled]);

  const [scanTask, setScanTask] =
    React.useState<ZoriPulseScanInvocation | null>(null);
  const [isInvoking, setIsInvoking] = React.useState(false);

  // Identifies the scan (if any) this hook instance itself started, and when.
  // A scan resumed from `detectInFlight` never populates this, so its
  // eventual terminal status is never reported as clinician-adoption
  // completion. `emittedCompletionTaskIdRef` guards against overlapping polls
  // or repeated terminal observations reporting the same completion twice.
  const localScanRef = React.useRef<{
    taskId: string;
    startedAt: number;
  } | null>(null);
  const emittedCompletionTaskIdRef = React.useRef<string | null>(null);

  const scanTasksQuery = apiHooks.useAPIQuery(
    'GET /v1/agents-v2/zori-audit/tasks',
    {
      agent: ZORI_PULSE_SCAN_AGENT_NAME,
      subject_id: patientId,
      limit: 200,
    },
    {
      enabled,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  );

  const lastCompletedScanAt = React.useMemo(() => {
    const tasks = Array.isArray(scanTasksQuery.data) ? scanTasksQuery.data : [];
    return tasks
      .filter(
        task =>
          getAuditSubjectMemberId(task.subjectId) === patientId &&
          task.status === 'completed' &&
          typeof task.created === 'number',
      )
      .reduce<number | null>(
        (latest, task) =>
          latest === null || task.created > latest ? task.created : latest,
        null,
      );
  }, [patientId, scanTasksQuery.data]);

  const notifyTerminalTask = React.useCallback(
    (task: ZoriPulseScanInvocation) => {
      if (task.status === 'completed') {
        notify(
          formatMessage(memberDetailMessages.zoriPulseScanComplete),
          'success',
        );
        return;
      }
      notify(formatMessage(memberDetailMessages.zoriPulseScanFailed), 'error');
    },
    [formatMessage, notify],
  );

  const triggerScan = React.useCallback(async () => {
    if (!enabled || !patientId || !projectId) {
      notify(formatMessage(memberDetailMessages.zoriPulseScanFailed), 'error');
      return;
    }

    const startedAt = Date.now();
    setIsInvoking(true);
    try {
      const response = await apiClient.request(
        'POST /v1/agents-v2/zori-pulse-scan/invoke',
        {
          subject_id: patientId,
          project_id: projectId,
          ...(scope ? { scope } : {}),
        },
      );
      localScanRef.current = { taskId: response.data.task_id, startedAt };
      setScanTask({ task_id: response.data.task_id, status: 'pending' });
      track('pulse_scan_requested', { entryPoint, outcome: 'accepted' });
    } catch {
      notify(
        formatMessage(memberDetailMessages.zoriPulseScanInvokeError),
        'error',
      );
      track('pulse_scan_requested', { entryPoint, outcome: 'error' });
    } finally {
      setIsInvoking(false);
    }
  }, [
    apiClient,
    enabled,
    entryPoint,
    formatMessage,
    notify,
    patientId,
    projectId,
    scope,
    track,
  ]);

  // Poll the active scan until it reaches a terminal status, then refresh the
  // last-run line so the row updates without a page reload.
  const scanTasksQueryRefetch = scanTasksQuery.refetch;
  React.useEffect(() => {
    if (!scanTask || ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(scanTask.status)) {
      return;
    }

    let isCancelled = false;
    let isPolling = false;

    const poll = async () => {
      if (isPolling) {
        return;
      }
      isPolling = true;
      try {
        const response = await apiClient.request(
          'GET /v1/agents-v2/zori-pulse-scan/invocations/:task_id',
          { task_id: scanTask.task_id },
        );
        if (isCancelled) {
          return;
        }
        const task = response.data;
        setScanTask(task);
        if (ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(task.status)) {
          notifyTerminalTask(task);
          void scanTasksQueryRefetch?.();
          onScanSettledRef.current?.();
          if (
            localScanRef.current?.taskId === task.task_id &&
            emittedCompletionTaskIdRef.current !== task.task_id
          ) {
            emittedCompletionTaskIdRef.current = task.task_id;
            track('pulse_scan_completed', {
              entryPoint,
              outcome: task.status as 'completed' | 'failed' | 'expired',
              durationBucket: bucketClientObservedDuration(
                localScanRef.current.startedAt,
              ),
            });
          }
        }
      } catch {
        if (isCancelled) {
          return;
        }
        // A transport error while polling does not mean the scan failed for
        // analytics (no `pulse_scan_completed` here), but it does end this
        // row's tracked scan the same way it always has: reset so the
        // operator can retry rather than polling forever against an outage.
        setScanTask(null);
        notify(
          formatMessage(memberDetailMessages.zoriPulseScanFailed),
          'error',
        );
      } finally {
        isPolling = false;
      }
    };

    const interval = window.setInterval(poll, ZORI_PULSE_SCAN_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(interval);
    };
  }, [
    apiClient,
    entryPoint,
    formatMessage,
    notify,
    notifyTerminalTask,
    scanTask,
    scanTasksQueryRefetch,
    track,
  ]);

  // On mount (when scanning is available), detect an already in-flight scan so
  // the button reflects the running state and resumes tracking it.
  React.useEffect(() => {
    if (!enabled || !patientId) {
      return;
    }

    let isCancelled = false;

    const detectInFlight = async () => {
      try {
        const response = await apiClient.request(
          'GET /v1/agents-v2/zori-pulse-scan/invocations',
          { subject_id: patientId },
        );
        if (isCancelled) {
          return;
        }
        const inFlight = response.data.find(
          task => !ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(task.status),
        );
        if (inFlight) {
          setScanTask(
            prev =>
              prev ?? { task_id: inFlight.task_id, status: inFlight.status },
          );
        }
      } catch {
        // Best-effort: ignore failures detecting an existing scan.
      }
    };

    void detectInFlight();

    return () => {
      isCancelled = true;
    };
  }, [apiClient, enabled, patientId]);

  const isRunning =
    isInvoking ||
    (!!scanTask && !ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(scanTask.status));

  return {
    lastCompletedScanAt,
    isLoadingLastRun: enabled && scanTasksQuery.isLoading,
    isRunning,
    triggerScan,
  };
};
